// src/app/components/AdditionalPropertiesGrid.tsx

import React, { useEffect, useId, useMemo, useRef } from 'react';
import { Field, ErrorMessage, useFormikContext } from 'formik';
import './AdditionalPropertiesGrid.scss';
import { SchemaProperty } from 'types/schema';
import type { RenderMode } from 'types/aspect';
import DateTimePickerInputAtom from '../../atoms/DateTimePickerInput/DateTimePickerInputAtom';
import { useTranslation } from 'localization';
import { getTranslation, filterHiddenPropertiesExcluding } from '@center-platform/spheres/identity';
import DropdownAtom, { DropdownChangeEvent } from '../../atoms/Dropdown/DropdownAtom';
import TooltipAtom from '../../atoms/Tooltip/TooltipAtom';
import { getSelectOptions } from '../../../utils/schemaPropertyOptions';
import SphereReferenceInputMolecule from '../SphereReference/SphereReferenceInputMolecule';
import { extractValuePlaceholderDeps } from '../SphereReference/resolveValuePlaceholders';
import UnifiedPropertyRendererMolecule from '../UnifiedPropertyRenderer/UnifiedPropertyRendererMolecule';
import { resolveRenderer, type ResolvedRenderer } from '../../../utils/resolveRenderer';
import { isMultiFieldLegacy, isMultiFieldChangeMapper, fanOutMultiFieldChange, setIfChanged } from '../../../utils/legacyMultiFieldFanout';
import { numberRuleConstraints } from '../../../utils/numberRuleConstraints';

interface SphereEditorFormValues {
  properties: Record<string, any>;
  assignedPerspectives?: any[];
}

interface AdditionalPropertiesGridProps {
  properties: SchemaProperty[];
  editable?: boolean;
  onDockedPropertyEdit?: (propertyName: string) => void;
  /** Property names whose write permission is denied. Rendered as disabled with a lock icon. */
  writeBlockedProps?: Set<string>;
  /** Render `dock: true` properties with their full editor; set inside a docked section's own tab. */
  renderDockedInline?: boolean;
}

const AdditionalPropertiesGrid: React.FC<AdditionalPropertiesGridProps> = ({ properties, editable = true, onDockedPropertyEdit, writeBlockedProps, renderDockedInline = false }) => {
  const { errors, values, setFieldValue } = useFormikContext<SphereEditorFormValues>();

  // Org the edited sphere belongs to — reference candidates resolve in this org,
  // not the caller's home org. Sourced from the sphere's own org-id property
  // (organization_id canonical, with the propagated sphereId as fallback). Falls
  // back to undefined (caller's own org) when neither is present.
  const sphereOrganizationId =
    (values.properties as any)?.organization_id ?? (values.properties as any)?.sphereId ?? undefined;

  // is_private is excluded — its dedicated UI is the lock icon in the
  // sphere header. It became a real schema property in the
  // property-access-controls migration; without this exclusion it would
  // auto-render as a generic switch in the grid.
  const filtered = filterHiddenPropertiesExcluding(properties, ['type', 'name', 'description', 'is_private']);

  // Map of dependent-reference-field → sibling property names it depends on via $value()
  const refDependencies = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const prop of filtered) {
      if (prop.type !== 'reference') continue;
      const deps = extractValuePlaceholderDeps((prop as any).metadata?.targetFilter);
      if (deps.length > 0) map.set(prop.name, deps);
    }
    return map;
  }, [filtered]);

  // Cascade-clear: when a sibling that a reference field depends on changes,
  // clear the dependent field so a stale selection isn't submitted.
  const prevPropsRef = useRef<Record<string, any> | null>(null);
  useEffect(() => {
    const current = values.properties ?? {};
    if (prevPropsRef.current === null) {
      prevPropsRef.current = { ...current };
      return;
    }
    const prev = prevPropsRef.current;
    for (const [fieldName, deps] of refDependencies.entries()) {
      const siblingChanged = deps.some((dep) => prev[dep] !== current[dep]);
      if (siblingChanged && current[fieldName] != null) {
        setFieldValue(`properties.${fieldName}`, null);
      }
    }
    prevPropsRef.current = { ...current };
  }, [values.properties, refDependencies, setFieldValue]);

  const { i18n, t } = useTranslation();

  // Why a row is locked: `read_only` fields are set by the system, `access.write` fields need a
  // permission the viewer lacks, and a caller may block a field for neither reason.
  const writeBlockedReason = (prop: SchemaProperty): string => {
    if (prop.read_only) return t('sphere.property.writeBlocked.readOnly');
    const permission = (prop as any).access?.write as string | undefined;
    return permission ? t('sphere.property.writeBlocked.tooltip', { permission }) : t('sphere.property.writeBlocked.generic');
  };
  // The lock is a keyboard stop whose tooltip opens on focus as well as hover, so the reason reaches
  // keyboard and touch users, not only a mouse hovering a `title`.
  const lockTipClass = `property-write-blocked-tip-${useId().replaceAll(/[^a-zA-Z0-9_-]/g, '')}`;

  // Enhanced onEdit handler that switches to docked tab for dock:true properties
  const handleEdit = (propertyName: string, prop: SchemaProperty) => {
    if ((prop as any).dock && onDockedPropertyEdit) {
      onDockedPropertyEdit(propertyName);
    }
  };

  /**
   * "Required" when the field is invalid and empty, else "Enter <name>" — the pre-unification
   * placeholder text the old grid computed once and threaded into both the select dropdown and the
   * plain string field (EAINT-855 review pass 2, M-5/M-6).
   */
  const computePlaceholder = (prop: SchemaProperty, hasError: boolean, propertyValue: unknown): string =>
    hasError && !propertyValue ? t('common.required') : t('sphere.property.placeholder', { propertyName: prop.name });

  /**
   * Extra props merged over whatever `resolveRenderer` resolved — schema-rule-derived
   * `min`/`max`/`integer`/`precision` a code-defined `TYPE_DEFAULT_RENDERERS` entry has no way to
   * express statically (EAINT-855 Phase 4), plus the `date`/`datetime` display formatting and
   * `date`-edit picker config the old grid computed inline and the string-field placeholder
   * (EAINT-855 review pass 2, M-5/M-6 — restored here, not as re-added call-site switches).
   *
   * The date/datetime/string additions are gated on `rendererSource === 'typeDefault'`: they are
   * the TYPE-DEFAULT's own hardcoded config, not schema-derived constraints like number/unit's
   * min/max (which apply no matter which component ends up mounted). An aspect's explicit
   * `renderers`/legacy `editComponent` for these types must still win untouched — forcing
   * `dateFormat`/`placeholder` over an explicit ref's own static `props` would silently override
   * it, the same class of regression SC2/SC4 exist to forbid.
   */
  const extraPropsFor = (
    prop: SchemaProperty,
    hasError: boolean,
    effectiveEditable: boolean,
    propertyValue: unknown,
    rendererSource: ResolvedRenderer['source']
  ): Record<string, unknown> => {
    // `editable` travels on EVERY property, not just number/unit. Legacy display/edit components
    // (SchedulesEditorMolecule, ChipListDisplayAtom, …) branch on it, and the shim forwards
    // `extraProps` straight through as their `componentProps`. Dropping it left them rendering in
    // their default state regardless of the grid's editability, while `SphereEditor`'s docked tabs
    // kept passing it — the exact cross-surface drift SC5 exists to kill.
    const common = { editable: effectiveEditable };

    if (prop.type === 'number') {
      const { min, max, integer, precision } = numberRuleConstraints(prop);
      return { ...common, numberType: integer ? 'integer' : 'decimal', min, max, precision, showButtons: true, invalid: hasError };
    }
    if (prop.type === 'unit') {
      const { min, max, integer, precision } = numberRuleConstraints(prop);
      const unitConfig = (prop as any).unitConfig || { category: 'time', defaultUnit: 'h', showSwitcher: true, displayPrecision: 2 };
      return { ...common, min, max, integer, precision, unitConfig, showStepper: (prop as any).showStepper !== false, invalid: hasError };
    }
    if (rendererSource !== 'typeDefault') return common;

    if (prop.type === 'date' && effectiveEditable) {
      // Hardcoded literals, not sourced from the property — matches the old grid's `date`-edit
      // branch (and `SphereFieldInputMolecule`'s own `date` bypass) byte-for-byte.
      return { ...common, dateFormat: 'yy-mm-dd', selectionMode: 'single' as const };
    }
    if ((prop.type === 'date' || prop.type === 'datetime') && !effectiveEditable) {
      const displayFormat = (prop as any).displayFormat as string | undefined;
      return {
        ...common,
        format: displayFormat || (prop.type === 'date' ? 'custom' : 'compact'),
        customFormat: prop.type === 'date' ? displayFormat || 'LL' : undefined,
        showTimezone: prop.type === 'datetime',
      };
    }
    if (prop.type === 'string' && effectiveEditable) {
      return { ...common, placeholder: computePlaceholder(prop, hasError, propertyValue) };
    }
    return common;
  };

  /**
   * Renders one property's edit/view control through the shared resolver
   * (`resolveRenderer`/`UnifiedPropertyRendererMolecule`) — the single fallback chain every
   * aspect/property render surface shares (EAINT-855 spec). Three type+mode combinations bypass
   * the resolver's TYPE-DEFAULT fallback step (never its explicit/legacy steps, which still win
   * per SC2/SC4) because a code-defined static type default cannot carry the per-render dynamic
   * context these need: `select`/`oneOf` options (only known from `prop.metadata`, not
   * expressible as a static `RendererRef.props`), `reference` candidate search scoping
   * (`siblingValues`/`organizationId`), and `datetime`'s IANA timezone identity (the `_tz`
   * sibling field — the ISO string a generic adapter emits loses it irrecoverably).
   */
  const renderPropertyInput = (prop: SchemaProperty, fieldName: string, propertyValue: any, effectiveEditable: boolean, hasError: boolean) => {
    // `dock: true` properties (SchedulesEditorMolecule, DataGridAtom, …) declare a heavyweight
    // `editComponent` that only belongs in the DOCKED TAB — `SphereEditor`'s dedicated editing
    // surface for the property. The grid itself must always resolve the collapsed
    // `displayComponent` (its `onEditProp` is exactly the affordance `handleEdit` wires to
    // `onDockedPropertyEdit`). Without this, edit mode resolves `editComponent` directly here too:
    // the full inline editor renders in BOTH the grid and the docked tab, and — because that
    // editor has no `onEdit` prop of its own — the dock affordance becomes unreachable
    // (EAINT-855 review pass 2, M-9).
    const isDocked = !!(prop as any).dock && !renderDockedInline;
    const mode: RenderMode = effectiveEditable && !isDocked ? 'edit' : 'view';
    const resolved = resolveRenderer(prop, mode);

    if (resolved.source === 'typeDefault' && mode === 'edit') {
      const selectOptions = getSelectOptions(prop);
      if (selectOptions) {
        return (
          <DropdownAtom
            value={propertyValue ?? null}
            onChange={(e: DropdownChangeEvent) => setFieldValue(fieldName, e.value)}
            options={selectOptions}
            optionLabel="label"
            optionValue="value"
            className="w-full"
            placeholder={computePlaceholder(prop, hasError, propertyValue)}
          />
        );
      }

      if (prop.type === 'reference') {
        return (
          <SphereReferenceInputMolecule
            property={prop}
            value={propertyValue ?? null}
            onChange={(id) => setFieldValue(fieldName, id ?? null)}
            editable={true}
            className="w-full"
            siblingValues={values.properties}
            organizationId={sphereOrganizationId}
          />
        );
      }

      if (prop.type === 'datetime') {
        return (
          <Field
            name={fieldName}
            as={DateTimePickerInputAtom}
            dateFormat={(prop as any).dateFormat || 'MM/dd/yy'}
            timezoneOptions={(prop as any).timezoneOptions}
            defaultTimezone={(prop as any).defaultTimezone}
            onTimezoneChange={(tz: string) => setFieldValue(`${fieldName}_tz`, tz)}
          />
        );
      }
    }

    const isMultiField = isMultiFieldLegacy(resolved);
    const rendererValue = isMultiField ? values : propertyValue;

    return (
      <UnifiedPropertyRendererMolecule
        property={prop}
        mode={mode}
        value={rendererValue}
        onChange={
          effectiveEditable
            ? (newValue: unknown) => {
                if (resolved.legacyShim) {
                  if (isMultiFieldChangeMapper(resolved)) {
                    // Multi-field ONLY fans out to the mapped paths. `newValue` here is
                    // `DesignSystemComponentRenderer`'s deep clone of the WHOLE Formik values bag
                    // (it was handed `values`, not the property value), so also writing it to
                    // `properties.<name>` would clobber the fan-out's own writes with the entire
                    // form. Matches the docked-tab handler in `SphereEditor` exactly (SC5).
                    fanOutMultiFieldChange(newValue, resolved.legacyShim.changeMapper, values, setFieldValue);
                  } else {
                    // Covers both the plain single-value shim AND a multiField `valueAccessor`
                    // with no `changeMapper` — `DesignSystemComponentRenderer` emits the latter's
                    // change as the child's raw payload (no fan-out mapping exists for it), so it
                    // must land at the property's own path rather than be silently dropped by
                    // `fanOutMultiFieldChange` no-oping on an undefined `changeMapper` (EAINT-855
                    // review pass 2, L-10).
                    setIfChanged(fieldName, newValue, propertyValue, setFieldValue);
                  }
                } else {
                  setFieldValue(fieldName, newValue);
                }
              }
            : undefined
        }
        onEdit={() => handleEdit(prop.name, prop)}
        extraProps={extraPropsFor(prop, hasError, effectiveEditable, propertyValue, resolved.source)}
        // The invalid affordance rides on the renderer WRAPPER rather than the input element:
        // every data type now renders through a different atom (and several through adapters that
        // expose no className), so the only place the grid can mark a field invalid uniformly is
        // the element it owns. `.is-invalid` is the grid's existing rule. `property-value` is the
        // old display-mode class every view render carried (EAINT-855 review pass 2, M-5/M-6) —
        // unstyled today, restored for byte-for-byte DOM parity rather than dropped as dead.
        className={[hasError ? 'is-invalid' : undefined, !effectiveEditable ? 'property-value' : undefined]
          .filter(Boolean)
          .join(' ') || undefined}
      />
    );
  };

  // Format a raw value for read-only display in a write-blocked cell.
  // • ISO datetimes  → "Mar 5, 2026 · 8:14 PM" (locale-aware, drops timezone clutter)
  // • UUIDs          → "da25ce03…"  (first 8 chars — still copy-pasteable via tooltip)
  // • Everything else → unchanged
  const formatDisplayValue = (value: unknown, locale = i18n.language): string => {
    if (value == null || value === '') return '';
    const str = String(value);

    // ISO 8601 datetime (with or without milliseconds / Z / offset)
    const isoPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/;
    if (isoPattern.test(str.trim())) {
      try {
        const d = new Date(str);
        if (!isNaN(d.getTime())) {
          const date = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' }).format(d);
          const time = new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(d);
          return `${date} · ${time}`;
        }
      } catch {
        // fall through to raw string
      }
    }

    // UUID — truncate to first 8 chars, full ID remains in title for copy
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (uuidPattern.test(str.trim())) {
      return `${str.slice(0, 8)}…`;
    }

    return str;
  };

  // Whether ALL visible properties are write-blocked — used to show the
  // "SYSTEM FIELDS" section eyebrow on the grid wrapper.
  const allWriteBlocked = filtered.length > 0 && filtered.every((p) => writeBlockedProps?.has(p.name));

  return (
    <div
      className="additional-properties-grid-component"
      data-all-write-blocked={allWriteBlocked || undefined}
    >
      {filtered.map((prop) => {
        const fieldName = `properties.${prop.name}`;
        const propertyValue = values.properties?.[prop.name];
        const hasError = !!errors.properties?.[prop.name];
        const isWriteBlocked = writeBlockedProps?.has(prop.name) ?? false;
        const effectiveEditable = editable && !isWriteBlocked;
        const label = getTranslation(i18n, prop.label_key, prop.label ?? '—');

        // In view mode, render UUIDs truncated and empty values as a dash.
        const shouldShowFormattedValue = !effectiveEditable && propertyValue != null;
        const formattedValue = shouldShowFormattedValue ? formatDisplayValue(propertyValue) : null;
        const isEmpty = propertyValue == null || propertyValue === '';

        const propertyInput = renderPropertyInput(prop, fieldName, propertyValue, effectiveEditable, hasError);

        const propertyLabel = (
          <label htmlFor={fieldName}>
            {label.charAt(0).toUpperCase() + label.slice(1)}
            {prop.required && <span className="required-indicator">*</span>}
          </label>
        );

        return (
          <div
            className={`property-item${isWriteBlocked ? ' property-item--write-blocked' : ''}`}
            key={prop.name}
            data-property-name={prop.name}
          >
            {isWriteBlocked ? (
              // The lock is a focusable control (the reason opens on focus), so it sits beside the label, not inside it.
              <div className="property-item__label-row">
                {propertyLabel}
                <button
                  type="button"
                  className={`pi pi-lock property-write-blocked-icon ${lockTipClass}`}
                  aria-label={writeBlockedReason(prop)}
                  data-pr-tooltip={writeBlockedReason(prop)}
                />
              </div>
            ) : (
              propertyLabel
            )}
            {/* For view-mode write-blocked fields: show formatted value (UUID truncated,
                timestamps humanised) or an em-dash for empty. The full raw value is kept
                in `title` so hovering reveals the original for copy-paste. */}
            {isWriteBlocked && !effectiveEditable ? (
              <span
                className={`property-value${isEmpty ? ' property-value--empty' : ''}`}
                title={isEmpty ? undefined : String(propertyValue)}
              >
                {formattedValue ?? ''}
              </span>
            ) : (
              propertyInput
            )}
            {effectiveEditable && <ErrorMessage name={fieldName}>{(msg) => <div className="error-message">{msg}</div>}</ErrorMessage>}
          </div>
        );
      })}
      {filtered.some((prop) => writeBlockedProps?.has(prop.name)) && (
        <TooltipAtom target={`.${lockTipClass}`} event="both" position="top" />
      )}
    </div>
  );
};

export default AdditionalPropertiesGrid;
